import '../../../../stylesheets/components/device_marker.scss';
import { createMarkerIconElement, getMarkerIcon } from '../../../services/DeviceMarkerIcons';

/**
 * Creates a Google Maps overlay that renders the configured device icon, status,
 * heading, selection artwork, and label as one interactive marker.
 */
const createDeviceMarker = ({ OverlayView = window.google.maps.OverlayView, ...args }) => {
  class DeviceMarker extends OverlayView {
    constructor() {
      super();
      this.position = args.position;
      this.title = args.title;
      this.heading = args.heading || 0;
      this.status = args.status || 'inactive';  // moving, stopped, idle, inactive
      this.isLocationLocked = args.isLocationLocked || false;
      this.addedToMap = false;
      this.mapMarkerIcon = args.mapMarkerIcon || 'arrow';
      this.selected = false;
      this.drawListeners = {};
      this.visible = true;

      this.setMap(args.map);
    }

    addDrawListener(key, cb) {
      this.drawListeners[key] = cb;
    }

    removeDrawListener(key) {
      delete this.drawListeners[key];
    }

    // Used to update the position of the marker given an object literal
    // containing latitude and longitude.
    updatePosition(newPosition) {
      if (!this.addedToMap) {
        // Since nothing has been initialized
        return;
      }

      const { latitude, longitude, heading } = newPosition;

      if (latitude !== undefined && latitude !== null
          && longitude !== undefined && longitude !== null) {
        this.position = new window.google.maps.LatLng(
          latitude,
          longitude,
        );
      }

      if (heading !== undefined && heading !== null) {
        this.heading = heading;
      }

      this.draw();
    }

    updateStatus(newStatus) {
      this.status = newStatus;

      // There's a chance the rootDiv hasn't yet been initiated
      if (this.statusIndicator) {
        this.statusIndicator.className = this.statusIndicatorClasses();
      }
    }


    setVisible(isVisible) {
      this.visible = isVisible;

      // There's a chance the rootDiv hasn't yet been initiated
      if (this.rootDiv) {
        this.rootDiv.style.visibility = isVisible ? 'visible' : 'hidden';
      }
    }

    /**
     * Applies the latest device position, state, visibility, and icon metadata.
     */
    update(obj) {
      this.updatePosition(obj.position);

      this.isLocationLocked = obj.isLocationLocked;
      this.updateStatus(obj.status);

      if (obj.mapMarkerIcon && obj.mapMarkerIcon !== this.mapMarkerIcon) {
        this.mapMarkerIcon = obj.mapMarkerIcon;
        this._renderMarkerIcon();
      }

      if (obj.visible !== undefined) {
        this.setVisible(obj.visible);
      }
    }

    statusIndicatorClasses() {
      if (this.isLocationLocked) {
        return 'status-symbol status-symbol--locked shieldgps-icon icon-lock';
      }

      const classes = ['status-symbol'];
      switch (this.status) {
        case 'moving':
          classes.push('status-symbol--moving');
          break;
        case 'stopped':
          classes.push('status-symbol--stopped');
          break;
        case 'idle':
          classes.push('status-symbol--idle');
          break;
        case 'sleeping':
          classes.push('status-symbol--sleeping');
          break;
        default:
          break;
      }

      return classes.join(' ');
    }

    // Inherited from Superclass.
    // Called whenever the map wants to render.
    draw() {
      this._positionRootDiv();
      this._rotateMarker();

      this._notifyDrawListeners();
    }

    _notifyDrawListeners() {
      const listeners = Object.values(this.drawListeners);
      listeners.forEach(l => l());
    }

    // Inherited from Superclass.
    // Called when marker has been successfully added to map and is ready
    // to be drawn.
    onAdd() {
      this._createRootDiv();
      this._appendDivToOverlay();
      this.addedToMap = true;
    }

    // Inherited from Superclass.
    // Called when the marker has been removed from the map.
    onRemove() {
      if (this.rootDiv) {
        this.rootDiv.parentNode.removeChild(this.rootDiv);
        this.rootDiv = null;
      }
    }

    // Need this to exposition location to InfoWindow etc.
    getPosition() {
      return this.position;
    }

    getDraggable() {
      return false;
    }

    getPositionInContainer() {
      return this.getProjection().fromLatLngToContainerPixel(this.position);
    }

    _positionRootDiv() {
      const point = this.getProjection().fromLatLngToDivPixel(this.position);

      if (point) {
        this.rootDiv.style.left = `${point.x - this.rootDiv.offsetWidth / 2}px`;
        this.rootDiv.style.top = `${point.y - this.rootDiv.offsetHeight / 2}px`;
      }
    }

    _rotateMarker() {
      if (!this.markerIcon) {
        return;
      }

      const icon = getMarkerIcon(this.mapMarkerIcon);
      const angle = icon.rotatesWithHeading ? this.heading + (icon.headingOffset || 0) : 0;
      this.markerIcon.style.transform = `rotate(${angle}deg)`;
    }

    /**
     * Switches both the marker artwork and title styling for map selection.
     */
    setSelected(selected) {
      if (!this.addedToMap) {
        return;
      }

      this.selected = selected;
      this.titleDiv.className = 'device-marker__title';
      if (selected) {
        this.titleDiv.className += ' device-marker__title--selected';
      }

      this.markerIcon.className = 'device-marker__icon';
      if (selected) {
        this.markerIcon.className += ' device-marker__icon--selected';
      }

      this._replaceMarkerIconElement();
      this._rotateMarker();
    }

    _replaceMarkerIconElement() {
      while (this.markerIcon.firstChild) {
        this.markerIcon.removeChild(this.markerIcon.firstChild);
      }
      this.markerIcon.appendChild(createMarkerIconElement(this.mapMarkerIcon, this.selected));
    }

    _renderMarkerIcon() {
      if (!this.markerIcon) {
        return;
      }

      this.markerIcon.className = 'device-marker__icon';
      if (this.selected) {
        this.markerIcon.className += ' device-marker__icon--selected';
      }
      this._replaceMarkerIconElement();
      this._rotateMarker();
    }

    _createRootDiv() {
      this.rootDiv = document.createElement('div');
      this.rootDiv.className = 'device-marker';
      this.rootDiv.style.visibility = this.visible ? 'visible' : 'hidden';

      this.markerIcon = document.createElement('div');
      this.markerIcon.className = 'device-marker__icon';
      this._renderMarkerIcon();

      this.titleDiv = document.createElement('div');
      this.titleDiv.className = 'device-marker__title';

      this.statusIndicator = document.createElement('span');
      this.statusIndicator.className = this.statusIndicatorClasses();
      this.titleDiv.appendChild(this.statusIndicator);

      this.titleDiv.appendChild(document.createTextNode(this.title));

      this.rootDiv.appendChild(this.markerIcon);
      this.rootDiv.appendChild(this.titleDiv);

      window.google.maps.event.addDomListener(this.rootDiv, 'click', () => {
        window.google.maps.event.trigger(this, 'click');

        // Stops the triggering of click event on map since this marker has
        // caught it. IMPORTANT to allow user to click outside marker to close
        // it
        window.event.stopPropagation();
      });
    }

    _appendDivToOverlay() {
      const panes = this.getPanes();
      panes.overlayMouseTarget.appendChild(this.rootDiv);
    }
  }

  return new DeviceMarker();
};

export default createDeviceMarker;
